// src/pages/DocumentDetailPage.js
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FileText,
  Download,
  Save,
  ArrowLeft,
  Building2,
  Truck,
  MapPin,
  ShieldCheck,
  Send,
} from "lucide-react";
import "./DocumentDetailPage.css";

const API_BASE = (process.env.REACT_APP_API_BASE || "https://app.kmaavfall.se/api").replace(/\/+$/, "");

function renderRow(label, value) {
  if (value === undefined || value === null || value === "") return null;

  return (
    <div className="docdetail-row">
      <div className="docdetail-label">{label}</div>
      <div className="docdetail-value">{String(value)}</div>
    </div>
  );
}

function fmtDateTime(value) {
  try {
    return new Date(value).toLocaleString("sv-SE");
  } catch {
    return value || "-";
  }
}

function getStatusLabel(status) {
  const s = String(status || "").trim().toLowerCase();

  if (s === "utkast") return "Utkast";
  if (s === "skickat") return "Skickat";
  if (s === "under behandling") return "Under behandling";
  if (s === "pågående") return "Pågående";
  if (s === "slutfört") return "Slutfört";
  if (s === "fakturerat") return "Fakturerat";

  return status || "Okänd";
}

function getStatusStyle(status) {
  const s = String(status || "").trim().toLowerCase();

  if (s === "skickat" || s === "slutfört") {
    return {
      background: "#e9f8ef",
      color: "#1f7a3d",
      border: "1px solid #cdeed8",
    };
  }

  if (s === "utkast") {
    return {
      background: "#fff8e8",
      color: "#9a6700",
      border: "1px solid #f3dfaa",
    };
  }

  return {
    background: "#eef3ff",
    color: "#3157a3",
    border: "1px solid #d9e4ff",
  };
}

function DocumentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);

  const [companyId, setCompanyId] = useState(null);
  const [extraCompanies, setExtraCompanies] = useState([]);
  const [selectedCompanyKey, setSelectedCompanyKey] = useState("current");

  const [signingBankId, setSigningBankId] = useState(false);
  const [bankIdHint, setBankIdHint] = useState("");
  const bankIdPollRef = useRef(null);

  useEffect(() => {
    fetchDoc();
    loadExtraCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    return () => {
      if (bankIdPollRef.current) {
        clearInterval(bankIdPollRef.current);
        bankIdPollRef.current = null;
      }
    };
  }, []);

  function clearBankIdPoll() {
    if (bankIdPollRef.current) {
      clearInterval(bankIdPollRef.current);
      bankIdPollRef.current = null;
    }
  }

  async function loadExtraCompanies() {
    try {
      const authRes = await fetch(`${API_BASE}/auth/me`, {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
      });

      const authRaw = await authRes.text();
      let authData = null;

      try {
        authData = authRaw ? JSON.parse(authRaw) : null;
      } catch {
        throw new Error(`Kunde inte läsa auth/me-svar`);
      }

      if (!authRes.ok || !authData?.ok) {
        throw new Error(authData?.error || "Kunde inte läsa inloggat företag");
      }

      const cid = Number(authData?.company?.id || authData?.user?.company_id || 0);

      if (!cid) {
        setCompanyId(null);
        setExtraCompanies([]);
        return;
      }

      setCompanyId(cid);

      const res = await fetch(`${API_BASE}/company-entities?companyId=${cid}`, {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
      });

      const raw = await res.text();
      let data;

      try {
        data = raw ? JSON.parse(raw) : [];
      } catch {
        throw new Error(`HTTP ${res.status}`);
      }

      if (!res.ok || data?.error) {
        throw new Error(data?.error || "Kunde inte läsa extra bolag");
      }

      const normalized = Array.isArray(data)
        ? data.map((company) => ({
            id: company.id,
            companyName: company.name || "",
            verksamhetsnamn: company.name || "",
            orgNumber: company.org_number || "",
            address: company.address || "",
            gatuadress: company.address || "",
            postalCode: company.postal_code || "",
            postnummer: company.postal_code || "",
            city: company.city || "",
            ort: company.city || "",
            contactPerson: company.contact_person || "",
            kontaktperson: company.contact_person || "",
            contactEmail: company.contact_email || "",
            kontaktEmail: company.contact_email || "",
            contactPhone: company.phone || "",
            kontaktTelefon: company.phone || "",
            isDefault: Boolean(company.is_default),
          }))
        : [];

      setExtraCompanies(normalized);
    } catch (e) {
      console.error("Kunde inte läsa extraCompanies från backend:", e);
      setExtraCompanies([]);
    }
  }

  async function fetchDoc() {
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE}/documents/${id}`, {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
      });

      const raw = await res.text();
      let data;

      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        throw new Error(`HTTP ${res.status}`);
      }

      if (!res.ok || data?.error) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      if (data) {
        const payload = data.payload || {};

        const normalizedPayload = {
          ...payload,
          companyAddress: payload.companyAddress || "",
          companyPostalCode: payload.companyPostalCode || "",
          companyCity: payload.companyCity || "",
        };

        setDoc({
          ...data,
          payload: normalizedPayload,
        });
        setStatus(data.status || "Under behandling");
        setNotes(data.notes || payload.notes || "");
        setSelectedCompanyKey("current");
      } else {
        setError("Kunde inte hitta dokumentet!");
        setDoc(null);
      }
    } catch (err) {
      console.error("Document fetch failed:", err);
      setError("Kunde inte hitta dokumentet!");
      setDoc(null);
    } finally {
      setLoading(false);
    }
  }

  function applySelectedCompanyToDoc(value) {
    setSelectedCompanyKey(value);

    if (value === "current") return;
    if (!doc) return;

    const selected = extraCompanies.find((company) => String(company.id) === String(value));
    if (!selected) return;

    setDoc((prev) => {
      if (!prev) return prev;

      const prevPayload = prev.payload || {};

      return {
        ...prev,
        payload: {
          ...prevPayload,
          companyName:
            selected.companyName ||
            selected.verksamhetsnamn ||
            prevPayload.companyName ||
            "",
          orgNumber: selected.orgNumber || prevPayload.orgNumber || "",
          contactPerson:
            selected.contactPerson ||
            selected.kontaktperson ||
            prevPayload.contactPerson ||
            "",
          contactEmail:
            selected.contactEmail ||
            selected.kontaktEmail ||
            prevPayload.contactEmail ||
            "",
          contactPhone:
            selected.contactPhone ||
            selected.kontaktTelefon ||
            prevPayload.contactPhone ||
            "",
          companyAddress:
            selected.address ||
            selected.gatuadress ||
            prevPayload.companyAddress ||
            "",
          companyPostalCode:
            selected.postalCode ||
            selected.postnummer ||
            prevPayload.companyPostalCode ||
            "",
          companyCity:
            selected.city ||
            selected.ort ||
            prevPayload.companyCity ||
            "",
          entity_id: selected.id || null,
        },
      };
    });
  }

  async function handleSave() {
    setSaving(true);
    setSaveMsg("");

    try {
      const payloadToSave = {
        ...(doc?.payload || {}),
        notes,
      };

      const updateRes = await fetch(`${API_BASE}/documents/${id}`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          status,
          notes,
          payload: payloadToSave,
        }),
      });

      const updateRaw = await updateRes.text();
      let updateData;

      try {
        updateData = updateRaw ? JSON.parse(updateRaw) : null;
      } catch {
        throw new Error("Ogiltigt svar från servern");
      }

      if (!updateRes.ok || updateData?.error) {
        throw new Error(updateData?.error || `HTTP ${updateRes.status}`);
      }

      setDoc(updateData?.document || {
        ...doc,
        status,
        notes,
        payload: payloadToSave,
      });

      setStatus(updateData?.document?.status || status);
      setNotes(updateData?.document?.notes || notes);
      setSaveMsg("Sparat! ✅");
    } catch (error) {
      console.error("Save failed:", error);
      setSaveMsg(`Fel: ${error.message || "Kunde inte spara ändringar."}`);
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(""), 2500);
    }
  }

  async function handleDownloadPdf() {
    try {
      setDownloading(true);

      const res = await fetch(`${API_BASE}/documents/${id}/pdf`, {
        method: "GET",
        headers: { Accept: "application/pdf" },
        credentials: "include",
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `transport_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert("Kunde inte skapa/ladda ner PDF just nu.");
    } finally {
      setDownloading(false);
    }
  }

  async function handleSendDocument() {
    const sendingAsNewCompany = selectedCompanyKey !== "current";

    const confirmed = window.confirm(
      sendingAsNewCompany
        ? "Du har valt ett annat bolag. Ett nytt dokument kommer skapas och skickas. Fortsätt?"
        : isSent
          ? "Är du säker att du vill skicka detta dokument igen till Naturvårdsverket?"
          : "Är du säker att du vill skicka detta dokument till Naturvårdsverket?"
    );

    if (!confirmed) return;

    try {
      setSending(true);
      setSaveMsg("");

      let targetId = id;

      // Om annat bolag är valt: skapa nytt dokument först
      if (sendingAsNewCompany) {
        const payloadToCreate = {
          ...(doc?.payload || {}),
          notes,
          // gör kopian tydlig i listan
          name: doc?.payload?.name || doc?.name || "Kopia transportdokument",
        };

        const prepareRes = await fetch(`${API_BASE}/transport/prepare`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(payloadToCreate),
        });

        const prepareRaw = await prepareRes.text();
        let prepareData;

        try {
          prepareData = prepareRaw ? JSON.parse(prepareRaw) : null;
        } catch {
          throw new Error(`Prepare HTTP ${prepareRes.status}`);
        }

        if (!prepareRes.ok || prepareData?.error || !prepareData?.id) {
          throw new Error(prepareData?.error || `Prepare HTTP ${prepareRes.status}`);
        }

        targetId = prepareData.id;
      } else {
        // Vanligt fall: spara ändringar på befintligt dokument innan skick
        const payloadToSave = {
          ...(doc?.payload || {}),
          notes,
        };

        const updateRes = await fetch(`${API_BASE}/documents/${id}`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            status,
            notes,
            payload: payloadToSave,
          }),
        });

        const updateRaw = await updateRes.text();
        const updateData = updateRaw ? JSON.parse(updateRaw) : null;

        if (!updateRes.ok || updateData?.error) {
          throw new Error(updateData?.error || `HTTP ${updateRes.status}`);
        }
      }

      const res = await fetch(`${API_BASE}/transport/finalize/${targetId}`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({}),
      });

      const raw = await res.text();
      let data;

      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        throw new Error(`HTTP ${res.status}`);
      }

      if (!res.ok || data?.error) {
        throw new Error(data?.details || data?.error || `HTTP ${res.status}`);
      }

      setSaveMsg(
        sendingAsNewCompany
          ? "Nytt dokument skapades och skickades ✅"
          : isSent
            ? "Dokument skickades igen till Naturvårdsverket ✅"
            : "Dokument skickat till Naturvårdsverket ✅"
      );

      if (sendingAsNewCompany) {
        navigate(`/documents/${targetId}`);
      } else {
        await fetchDoc();
      }
    } catch (err) {
      console.error("Send document failed:", err);
      setSaveMsg(`Fel vid skickning: ${err.message}`);
    } finally {
      setSending(false);
      setTimeout(() => setSaveMsg(""), 3500);
    }
  }

  async function handleSignWithBankId() {
    if (!doc) return;

    if (selectedCompanyKey !== "current") {
      alert(
        "Du har valt ett annat bolag. Skicka dokumentet som ett nytt dokument om det ska gälla det bolaget."
      );
      return;
    }

    try {
      clearBankIdPoll();
      setSigningBankId(true);
      setBankIdHint("");
      setSaveMsg("Startar BankID...");

      const res = await fetch(`${API_BASE}/documents/${id}/bankid/start`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          role: "transporter",
          personalNumber: doc?.payload?.personnummer || undefined,
          platform: "document-detail",
        }),
      });

      const raw = await res.text();
      let data;

      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        throw new Error(`Start HTTP ${res.status}`);
      }

      if (!res.ok || data?.error || !data?.orderRef) {
        throw new Error(data?.details || data?.error || `Start HTTP ${res.status}`);
      }

      setSaveMsg("BankID startat. Slutför signeringen i BankID-appen.");

      if (data.appLink) {
        try {
          window.location.href = data.appLink;
        } catch {
          window.open(data.appLink, "_blank", "noopener,noreferrer");
        }
      }

      startBankIdPolling(data.orderRef);
    } catch (err) {
      console.error("BankID start failed:", err);
      setSigningBankId(false);
      setSaveMsg(`Fel vid BankID-start: ${err.message}`);
      setTimeout(() => setSaveMsg(""), 3500);
    }
  }

  function startBankIdPolling(orderRef) {
    clearBankIdPoll();

    bankIdPollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/documents/${id}/bankid/collect`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            role: "transporter",
            orderRef,
          }),
        });

        const raw = await res.text();
        let data;

        try {
          data = raw ? JSON.parse(raw) : null;
        } catch {
          throw new Error(`Collect HTTP ${res.status}`);
        }

        if (!res.ok || data?.error) {
          throw new Error(data?.details || data?.error || `Collect HTTP ${res.status}`);
        }

        if (data.status === "pending") {
          setBankIdHint(data.hintCode || "");
          return;
        }

        if (data.status === "complete") {
          clearBankIdPoll();
          setSigningBankId(false);
          setBankIdHint("");
          setSaveMsg("Dokumentet är signerat med BankID ✅");
          await fetchDoc();
          setTimeout(() => setSaveMsg(""), 3500);
          return;
        }

        if (data.status === "failed") {
          clearBankIdPoll();
          setSigningBankId(false);
          setBankIdHint(data.hintCode || "");
          setSaveMsg("BankID-signering misslyckades.");
          setTimeout(() => setSaveMsg(""), 3500);
        }
      } catch (err) {
        console.error("BankID collect failed:", err);
        clearBankIdPoll();
        setSigningBankId(false);
        setSaveMsg(`Fel vid BankID-status: ${err.message}`);
        setTimeout(() => setSaveMsg(""), 3500);
      }
    }, 2000);
  }

  if (loading) {
    return (
      <div className="docdetail-container">
        <div className="docdetail-card">
          <p>Laddar dokument...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="docdetail-container">
        <div className="docdetail-card">
          <h2>{error}</h2>
          <button className="docdetail-btn" onClick={() => navigate(-1)}>
            Tillbaka
          </button>
        </div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="docdetail-container">
        <div className="docdetail-card">
          <h2>Dokument hittades inte</h2>
          <button className="docdetail-btn" onClick={() => navigate(-1)}>
            Tillbaka
          </button>
        </div>
      </div>
    );
  }

  const p = doc.payload || {};
  const fullName = [p.firstName, p.lastName].filter(Boolean).join(" ");
  const amountWithUnit = [p.amount, p.unit].filter(Boolean).join(" ");
  const sigTransportor = doc.signatures?.transporter;
  const isSigned = Boolean(sigTransportor);
  const currentStatusStyle = getStatusStyle(status);
  const isSent = String(status || "").trim().toLowerCase() === "skickat";

  return (
    <div className="docdetail-container">
      <div className="docdetail-card">
        <button
          className="docdetail-back"
          onClick={() => navigate(-1)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 18,
            background: "none",
            border: "none",
            cursor: "pointer",
            fontWeight: 700,
            color: "#217a43",
          }}
        >
          <ArrowLeft size={18} />
          Tillbaka
        </button>

        <div
          className="docdetail-header"
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 18,
          }}
        >
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
            {p.logo && p.logo.startsWith("data:image") ? (
              <img
                src={p.logo}
                alt="Logotyp"
                className="docdetail-logo"
                style={{
                  width: 58,
                  height: 58,
                  objectFit: "contain",
                  borderRadius: 10,
                }}
              />
            ) : (
              <div
                style={{
                  width: 58,
                  height: 58,
                  borderRadius: 14,
                  background: "#edf7f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#217a43",
                  flexShrink: 0,
                }}
              >
                <FileText size={22} />
              </div>
            )}

            <div>
              <h2 className="docdetail-title" style={{ marginBottom: 8 }}>
                {doc.name || p.companyName || p.wasteTypes || doc.wasteTypes || "Transportdokument"}
              </h2>

              <div
                className="docdetail-meta"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 14,
                  color: "#5f6b66",
                  fontSize: 14,
                }}
              >
                <span><b>Referens:</b> {doc.id}</span>
                <span><b>Skapad:</b> {doc.created_at ? fmtDateTime(doc.created_at) : "-"}</span>
                <span><b>Upph.datum:</b> {p.pickupDate || doc.pickupDate || "-"}</span>
              </div>
            </div>
          </div>

          <div
            style={{
              padding: "8px 12px",
              borderRadius: 999,
              fontWeight: 700,
              fontSize: 14,
              ...currentStatusStyle,
            }}
          >
            {getStatusLabel(status)}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 10,
            marginBottom: 20,
          }}
        >
          <div style={{ background: "#f8faf8", borderRadius: 12, padding: "12px 14px" }}>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Avfallskod</div>
            <div style={{ fontWeight: 700 }}>{p.wasteCode || "—"}</div>
          </div>

          <div style={{ background: "#f8faf8", borderRadius: 12, padding: "12px 14px" }}>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Mängd</div>
            <div style={{ fontWeight: 700 }}>{amountWithUnit || "—"}</div>
          </div>

          <div style={{ background: "#f8faf8", borderRadius: 12, padding: "12px 14px" }}>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Företag</div>
            <div style={{ fontWeight: 700 }}>{p.companyName || "—"}</div>
          </div>

          <div style={{ background: "#f8faf8", borderRadius: 12, padding: "12px 14px" }}>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Organisationsnummer</div>
            <div style={{ fontWeight: 700 }}>{p.orgNumber || "—"}</div>
          </div>
        </div>

        <div
          className="docdetail-info"
          style={{
            marginBottom: 20,
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "center",
          }}
        >
          <div>
            <b>Status:</b>{" "}
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              style={{
                marginLeft: 8,
                fontWeight: 700,
                borderRadius: 8,
                padding: "6px 10px",
                border: "1px solid #d8e0da",
                background: "#fff",
              }}
            >
              <option value="Utkast">Utkast</option>
              <option value="Under behandling">Under behandling</option>
              <option value="Pågående">Pågående</option>
              <option value="Skickat">Skickat</option>
              <option value="Slutfört">Slutfört</option>
              <option value="Fakturerat">Fakturerat</option>
            </select>
          </div>
        </div>

        <div
          className="docdetail-sections"
          style={{
            display: "grid",
            gap: 16,
          }}
        >
          <section className="docdetail-section">
            <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Truck size={18} />
              Transportuppgifter
            </h3>
            {renderRow("Avfallskod", p.wasteCode)}
            {renderRow("Avfallstyp(er)", p.wasteTypes || doc.wasteTypes)}
            {renderRow("Mängd", amountWithUnit)}
            {renderRow("Upphämtningsadress", p.pickupAddress)}
            {renderRow("Upphämtningsdatum", p.pickupDate || doc.pickupDate)}
            {renderRow("Plats där avfallet lämnas", p.deliveryPlace)}
            {renderRow("Anteckningar (ärende)", p.notes)}
          </section>

          <section className="docdetail-section">
            <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <FileText size={18} />
              Uppgiftslämnare (transportör)
            </h3>
            {renderRow("Namn", fullName)}
            {renderRow("Personnummer", p.personnummer)}
            {renderRow("Telefon", p.phone)}
            {renderRow("E-post", p.email)}
            {renderRow("Transportsätt", p.transportsatt)}
          </section>

          <section className="docdetail-section">
            <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Building2 size={18} />
              Företag
            </h3>

            <div className="docdetail-row">
              <div className="docdetail-label">Välj bolag</div>
              <div className="docdetail-value">
                <select
                  value={selectedCompanyKey}
                  onChange={(e) => applySelectedCompanyToDoc(e.target.value)}
                  style={{
                    fontWeight: 700,
                    borderRadius: 8,
                    padding: "6px 10px",
                    border: "1px solid #d8e0da",
                    background: "#fff",
                    width: "100%",
                    maxWidth: 280,
                  }}
                  disabled={saving || sending || signingBankId || !companyId}
                >
                  <option value="current">
                    {p.companyName || "Nuvarande bolag"}
                  </option>
                  {extraCompanies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.verksamhetsnamn || company.companyName || "Namnlöst bolag"}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {renderRow("Företagsnamn", p.companyName)}
            {renderRow("Organisationsnummer", p.orgNumber)}
            {renderRow("Kontaktperson", p.contactPerson)}
            {renderRow("Kontaktperson e-post", p.contactEmail)}
            {renderRow("Kontaktperson tel", p.contactPhone)}
            {renderRow("Gatuadress", p.companyAddress)}
            {renderRow("Postnummer", p.companyPostalCode)}
            {renderRow("Ort", p.companyCity)}
            {renderRow("CFAR", p.cfar)}
          </section>

          <section className="docdetail-section">
            <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <MapPin size={18} />
              Plats för avfallets uppkomst
            </h3>
            {renderRow("Namn", p.platsNamn)}
            {renderRow("Adress", p.platsAdress)}
            {renderRow("Postnummer", p.platsPostnummer)}
            {renderRow("Kommun", p.platsKommun)}
            {renderRow("Gatuadress", p.platsGatuadress)}
            {renderRow("X-koordinat", p.platsX)}
            {renderRow("Y-koordinat", p.platsY)}
          </section>

          <section className="docdetail-section">
            <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <ShieldCheck size={18} />
              Underskrift (BankID)
            </h3>

            <div className="docdetail-row">
              <div className="docdetail-label">Transportör</div>
              <div className="docdetail-value">
                {sigTransportor ? (
                  <>
                    ✅ {sigTransportor.name || "Signerad"} — {fmtDateTime(sigTransportor.signed_at)}
                  </>
                ) : (
                  <>⏳ Ej signerad</>
                )}
              </div>
            </div>

            {!isSigned && (
              <div style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="docdetail-btn"
                  onClick={handleSignWithBankId}
                  disabled={signingBankId || saving || sending}
                  style={{
                    background: "#217a43",
                    color: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    opacity: signingBankId || saving || sending ? 0.65 : 1,
                  }}
                >
                  <ShieldCheck size={16} />
                  {signingBankId ? "Väntar på BankID..." : "Signera detta dokument"}
                </button>

                {bankIdHint && (
                  <div style={{ marginTop: 8, color: "#6b7280", fontSize: 14 }}>
                    BankID-status: {bankIdHint}
                  </div>
                )}
              </div>
            )}

            {doc.verification_id && (
              <div className="docdetail-row" style={{ marginTop: 8 }}>
                <div className="docdetail-label">Verifierings-ID</div>
                <div className="docdetail-value">{doc.verification_id}</div>
              </div>
            )}
          </section>

          <section className="docdetail-section">
            <h3>Interna anteckningar</h3>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={5}
              style={{
                width: "100%",
                borderRadius: 10,
                border: "1px solid #d8e0da",
                padding: 12,
                fontSize: 15,
              }}
            />
          </section>
        </div>

        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginTop: "1.2rem",
          }}
        >
          <button
            className="docdetail-btn"
            onClick={handleSave}
            disabled={saving || sending || signingBankId}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Save size={16} />
            {saving ? "Sparar..." : "Spara ändringar"}
          </button>

          <button
            className="docdetail-btn"
            onClick={handleDownloadPdf}
            disabled={downloading || sending || signingBankId}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Download size={16} />
            {downloading ? "Skapar PDF…" : "Ladda ner PDF"}
          </button>

          <button
            className="docdetail-btn"
            type="button"
            style={{
              background: "#217a43",
              color: "#fff",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
            onClick={handleSendDocument}
            disabled={sending || saving || signingBankId}
          >
            <Send size={16} />
            {sending ? "Skickar..." : isSent ? "Skicka igen" : "Skicka dokument"}
          </button>
        </div>

        {saveMsg && (
          <div
            style={{
              marginTop: 10,
              color: saveMsg.toLowerCase().includes("fel") ? "#a12828" : "green",
            }}
          >
            {saveMsg}
          </div>
        )}
      </div>
    </div>
  );
}

export default DocumentDetailPage;